import { useState, FC, forwardRef, useEffect } from 'react';

import { Popover } from 'react-tiny-popover';

import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import {
  SearchBarInputProps,
  SearchBarItemProps,
} from '@components/ui/search/SearchBarInput/SearchBarInputInterface';
import { StyledSearchBarWrapper } from '@components/ui/search/SearchBarInput/SearchBarInputStyle';
import SelectedComponent from '@shared/searchBar/datePicker/selectedComponent';
import EmptyComp from '@shared/searchBar/datePicker/emptyComp';
import { formatShortLocalDate } from '@utils/dateUtil';
import useMountedRef from '@hooks/useMountedRef';

import {
  HotelCalendarOverlayContentWrapper,
  SelectedCompWrapper,
} from './styled';

export const HOTEL_POPOVER_DEFAULT_CONTAINER_STYLE: Partial<CSSStyleDeclaration> =
  {
    zIndex: '99',
    backgroundColor: 'white',
    width: '575px',
    display: 'flex',
    justifyContent: 'center',
    margin: '0px',
    boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
    top: '-8px',
  };

export const resolveHotelPopoverContainerStyle = (
  containerStyle: Partial<CSSStyleDeclaration> | undefined,
  isPopoverContentVisible: boolean
): Partial<CSSStyleDeclaration> => ({
  ...HOTEL_POPOVER_DEFAULT_CONTAINER_STYLE,
  ...containerStyle,
  opacity: isPopoverContentVisible ? '1' : '0',
  pointerEvents: isPopoverContentVisible ? 'auto' : 'none',
});

export const SearchBarItem: FC<SearchBarItemProps> = ({
  InputComponent,
  error,
}) => {
  const dateData = InputComponent?.toString().split('-');
  const startDate = dateData?.[0];
  const endDate = dateData?.[1];
  const hasEndDate = endDate !== ' ' ? true : false;

  return (
    <SelectedCompWrapper isError={error && !hasEndDate}>
      {!startDate || startDate === ' ' ? (
        <EmptyComp label='Giriş' />
      ) : (
        <SelectedComponent
          value={formatShortLocalDate(startDate) as string}
          label='Giriş'
          rightBorder
        />
      )}
      {!endDate || endDate === ' ' ? (
        <EmptyComp label='Çıkış' />
      ) : (
        <SelectedComponent
          value={formatShortLocalDate(endDate) as string}
          label='Çıkış'
          rightBorder
        />
      )}
    </SelectedCompWrapper>
  );
};

export interface CustomPopoverChildComponentProps
  extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
}

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper
    ref={ref}
    onClick={props.onClick}
    data-testid='hotel-date-selection-button'
  >
    {props.children}
  </StyledSearchBarWrapper>
));

const HotelSearchBarInput: FC<SearchBarInputProps> = ({
  InputComponent,
  renderOverlayContent,
  error,
  onFocus,
  onBlur,
  showLabel,
  popoverContentReady = true,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    containerStyle,
    ...otherPopoverProps
  } = {},
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);

  const { parentRef, isParentReady } = useMountedRef<HTMLDivElement>();

  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = () => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  };

  const toggleOverlay = () => {
    isControlledPopover
      ? onVisibleChange?.(!isOpen)
      : setIsOverlayOpen(!isOverlayOpen);
  };

  const isOverlayVisible = isControlledPopover ? !!isOpen : isOverlayOpen;
  const isPopoverContentVisible = isOverlayVisible && popoverContentReady;
  const resolvedContainerStyle = resolveHotelPopoverContainerStyle(
    containerStyle,
    isPopoverContentVisible
  );

  useEffect(() => {
    if (isOverlayVisible) {
      onFocus?.();
    } else {
      onBlur?.();
    }
  }, [isOverlayVisible]);

  return (
    <div ref={parentRef}>
      {isParentReady && parentRef.current ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={padding}
          containerStyle={resolvedContainerStyle}
          reposition={false}
          parentElement={parentRef?.current || undefined}
          content={() => {
            return renderOverlayContent ? (
              <HotelCalendarOverlayContentWrapper
                aria-hidden={!isPopoverContentVisible}
                inert={!isPopoverContentVisible ? true : undefined}
              >
                {renderOverlayContent(closeOverlay)}
              </HotelCalendarOverlayContentWrapper>
            ) : (
              <div /> //TODO: it will never render, ts null error fix
            );
          }}
          isOpen={isOverlayVisible}
          onClickOutside={(e) => {
            onClickOutside?.(e);
            closeOverlay();
          }}
          {...otherPopoverProps}
        >
          <CustomPopoverChildComponent onClick={toggleOverlay}>
            <LocationAnimationErrorBox hasError={error || false}>
              <SearchBarItem
                InputComponent={InputComponent}
                error={error}
                showLabel={showLabel}
              />
            </LocationAnimationErrorBox>
          </CustomPopoverChildComponent>
        </Popover>
      ) : null}
    </div>
  );
};

export default HotelSearchBarInput;
